import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { XCircle, AlertCircle } from "lucide-react";

interface UnsuccessfulProposal {
  id: string;
  rfp_id: string;
  supplier_company_name: string;
  initial_bid_amount: number;
  invitation_id: string;
  rfps: {
    title: string;
    description: string;
    matter_name: string | null;
  };
  supplier_invitations?: {
    invite_token: string;
  };
}

const SupplierUnsuccessfulProposals = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<UnsuccessfulProposal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchUnsuccessfulProposals();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchUnsuccessfulProposals = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("proposals")
        .select(`
          id,
          rfp_id,
          supplier_company_name,
          initial_bid_amount,
          invitation_id,
          rfps (
            title,
            description,
            matter_name
          )
        `)
        .eq("user_id", user.id)
        .eq("selection_status", "unsuccessful");

      if (error) throw error;

      // Get invite tokens for navigation
      const proposalIds = (data || []).map(p => p.invitation_id);
      const { data: invitations } = await supabase
        .from("supplier_invitations")
        .select("id, invite_token")
        .in("id", proposalIds);

      const inviteMap = new Map((invitations || []).map(i => [i.id, i.invite_token]));

      const proposalsWithTokens = (data || []).map(p => ({
        ...p,
        supplier_invitations: { invite_token: inviteMap.get(p.invitation_id) || "" }
      }));

      setProposals(proposalsWithTokens);
    } catch (err) {
      console.error("Error fetching unsuccessful proposals:", err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <XCircle className="h-8 w-8 text-red-500" />
            Unsuccessful Proposals
          </h1>
          <p className="text-muted-foreground mt-2">Proposals where another bidder was selected</p>
        </div>

        {proposals.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No unsuccessful proposals</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {proposals.map((proposal) => (
              <Card key={proposal.id} className="hover:shadow-lg transition-shadow border-red-200 bg-red-50/30 flex flex-col">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{proposal.rfps?.matter_name || proposal.rfps?.title}</CardTitle>
                    <Badge variant="destructive">
                      <XCircle className="h-3 w-3 mr-1" />
                      Not Selected
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 flex-1 flex flex-col">
                  <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                    {proposal.rfps?.description}
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Your Bid</span>
                      <span className="font-semibold">${proposal.initial_bid_amount.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="mt-auto pt-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/rfp/${proposal.rfp_id}/bid/${proposal.supplier_invitations?.invite_token}`)}
                      className="w-full"
                    >
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierUnsuccessfulProposals;
